import { t } from 'vitest/dist/global-732f9b14';
import { prependRemoved, removeFrom } from './arrays';
import { CptModifier, findCptModifier } from './cpt-modifiers';
import { dateFromInput, formatLocalDate } from './date-time';
import {
  setAllDiagnosesOnEmProcedures,
  setAllDiagnosesOnEmptyCpts,
  setFirstDiagnosisOnEmptyCpts,
  setProcedureDiagnoses,
} from './diagnosis';
import { CodeSet, Diagnosis, Flags, Person, Procedure, Provider, Worksheet } from './domain';

export function procedureSummary(
  p: Procedure,
  showCodes = false,
  skipMetaData = false
): string {
  let result: string = p.cptCode;

  p.multiplier = p.multiplier || 1;
  if (p.multiplier) {
    result += 'x' + p.multiplier;
  }

  if (p.modifiers && p.modifiers.length) {
    result += ' / ';
    result += p.modifiers.join(',');
  }

  if (skipMetaData) {
    result += ' / ' + p.links;
    return result;
  } else {
    result += ' / ';

    if (showCodes) {
      if (p.diagnosisList && p.diagnosisList.length) {
        result += p.diagnosisList.join(',');
        result += ' / ';
      }
    } else {
      result += (p.links || '') + '/';
    }
  }

  // TODO ask Tom if there should be a separate observation date as in legacy
  if (p.isObservation && p.dateOfService instanceof Date) {
    result += `OB ${
      p.dateOfService.getMonth() + 1
    }-${p.dateOfService.getDate()}-${p.dateOfService.getFullYear()} `;
    result += ' / ';
  }

  if (p.serviceDate) {
    result += `SD ${formatLocalDate(p.serviceDate)} / `;
  }

  if (p.globalDays) {
    result += `GD ${p.globalDays} / `;
  }

  if (p.ndcInformation) {
    result += `NDC ${p.ndcInformation?.ndcNumber} / `;
  }

  if (p.serviceLocation) {
    result += `SL ${p.serviceLocation?.description} / `;
  }

  return result;
}

export function modifierLinkString(p: Procedure): string {
  if (!p.modifiers) {
    return '';
  }
  return p.modifiers.join(',');
}

export interface ModifierEval {
  links?: string;
  modifiers?: CptModifier[];
  custom?: CptModifier[];
  message?: string;
  codes?: string[];
}

export function evaluateModifierLinks(links: string): ModifierEval | null {
  if (!links) {
    return null;
  }
  if (!links.trim().length) {
    return null;
  }
  const deDupe: Map<string, string> = new Map();
  links.split(',').forEach((id) => {
    id = id.trim().toUpperCase();
    deDupe.set(id, id);
  });

  const result: ModifierEval = { links: '', modifiers: [], custom: [] };
  deDupe.forEach((id) => {
    const mod = findCptModifier(id);
    if (mod) {
      result.modifiers.push(mod);
      if (mod.custom) {
        result.custom.push(mod);
      }
    }
  });

  if (!result.modifiers.length) {
    return null;
  }
  result.links = result.modifiers.map((mod) => mod.code).join(',');

  result.codes = result.links.split(',');

  if (result.custom.length) {
    result.message =
      result.custom.length === 1
        ? `Warning: The follow modifier is not a legal billing modifier but a custom modifier: ${result.custom[0].code}.`
        : `Warning: The follow modifiers are not legal billing modifier but custom modifiers:  ${result.custom
            .map((mod) => mod.code)
            .join(',')}.`;
  }
  return result;
}

export function setProcedureModifiers(p: Procedure, links?: string): void {
  if (!p) {
    return;
  }
  const result: ModifierEval = evaluateModifierLinks(p.modifier || links);
  if (result) {
    p.modifier = result.links;
  } else {
    p.modifier = '';
  }
  p.modifiers = result ? result.codes : [];
}

export const CRITICAL_CARE_CODE_1 = '99291';
export const CRITICAL_CARE_CODE_2 = '99292';
export const CRITICAL_CARE_CODE_3 = '99281';

export interface CriticalCare {
  cpt1: boolean;
  cpt2: boolean;
  multiplier?: number;
  add: boolean;
}

export function nonCritical(p: Procedure): boolean {
  const code = parseInt(p.cptCode, 10);
  return code >= 99281 && code <= 99285;
}

export function observation(p: Procedure): boolean {
  const code = parseInt(p.cptCode, 10);
  if (code > 99217 && code <= 99220) {
    return true;
  }
  if (code >= 99234 && code <= 99236) {
    return true;
  }
  return false;
}

export const critical = (p: Procedure): boolean => p.cptCode === '99291';
export const criticalAddon = (p: Procedure): boolean => p.cptCode === '99292';
export const observationAddon = (p: Procedure): boolean =>
  p.cptCode === '99217';
export const compareCptCodesDes = (a: Procedure, b: Procedure) =>
  a.cptCode.localeCompare(b.cptCode);
export const compareCptCodesAsc = (a: Procedure, b: Procedure) =>
  a.cptCode.localeCompare(b.cptCode) * -1;
export const notEm = (p: Procedure): boolean => p.cptCode.charAt(0) != '9';

export function urgentCare(a: Procedure): boolean {
  var code: Number = new Number(a.cptCode);
  // @ts-ignore
  if (code >= 99201 && code <= 99205) return true;
  // @ts-ignore
  if (code >= 99211 && code <= 99215) return true;
  return false;
}


export function updateEmProcedures(list: Procedure[]): Procedure[] {
  let result = list;
  // Prolonged Em Procedure
  result = prependRemoved(result, (p) => p.cptCode == '99417');
  //TODO ask Nicki about sorting UC cpts
  result = prependRemoved(result, urgentCare);
  result = prependRemoved(result, observationAddon, compareCptCodesAsc);
  result = prependRemoved(result, observation, compareCptCodesDes);
  result = prependRemoved(
    result,
    (cpt) => criticalAddon(cpt) || critical(cpt),
    compareCptCodesDes
  );
  result = prependRemoved(result, nonCritical, compareCptCodesDes);
  return result;
}

interface CC {
  nonCritical: Procedure[];
}
export let currentNonCritical: Procedure[];

export function updateCriticalCare(
  cpts: Procedure[],
  criticalCare: CriticalCare,
  cpt1: Procedure,
  cpt2: Procedure
): Procedure[] {
  let list = cpts.slice();
  removeFrom(
    list,
    (p) =>
      p.cptCode === CRITICAL_CARE_CODE_1 ||
      p.cptCode === CRITICAL_CARE_CODE_2 ||
      p.cptCode === CRITICAL_CARE_CODE_3
  );

  const { multiplier } = criticalCare;
  if (criticalCare.cpt1) {
    cpt1.multiplier = 1;

    list.push({ ...cpt1 });

    if (criticalCare.cpt2) {
      cpt2.multiplier = criticalCare.multiplier;

      list.push({ ...cpt2, ...{ multiplier } });
    } else {
      removeFrom(list, (p) => p.cptCode === CRITICAL_CARE_CODE_2);
    }
  } 

  return updateEmProcedures(list);
}

export function updateCriticalCareV2(
  cpts: Procedure[],
  critCareCpts: Procedure[],
  provider: Provider,
  secondary: Provider,
  tertiary: Provider
): Procedure[] {
  let list = cpts.slice();
  removeFrom(
    list,
    (p) =>
      p.cptCode === CRITICAL_CARE_CODE_1 ||
      p.cptCode === CRITICAL_CARE_CODE_2 
  );

  critCareCpts.forEach((p) => {
    p.provider = provider;
    p.secondaryProvider = secondary;
    p.tertiaryProvider = tertiary;
    list.push({ ...p });
  });

  return updateEmProcedures(list);
}


export function hasBeenBilled(p: Procedure): boolean {
  if (!p?.billingInformation) {
    return false;
  }
  return p?.billingInformation.isBilled;
}

export function procedure(
  c: Partial<CodeSet> | Partial<Procedure>  | any,
  workflow?: string
): Procedure {
  const { note } = c;

  setProcedureModifiers(c);

  c.multiplier = c.multiplier || 1;

  const globalDays =
    c.globalDays ||
    (c?.rvuInfoList?.length ? c?.rvuInfoList[0].globalDays : undefined);

  const description =
    typeof c.description === 'object'
      ? c.description.longDescription || c.description.description
      : c.description || c.longDescription;

  let auditMap = c.auditMap || {};

  if (workflow) {
    auditMap[workflow] = {...c.audit || {}}
    auditMap[workflow].disagreementReasons = undefined;
  }

  const flags: Flags = c.flags || {};
  return {
    auditMap,
    globalDays,
    description,
    audit: c.audit || {},
    procedureId: c?.procedureId,
    promptNDC: c?.promptNDC || flags?.promptNDC || false,
    isEMCode: c.isEMCode || c.em || flags?.em || false,
    isBilled: c?.isBilled || false,
    isObservation: c?.isObservation || flags.obs || false,
    isMIPSCode: c.isMIPSCode || false,
    billingInformation: c?.billingInformation,
    ndcInformation: c?.ndcInformation,
    modifier: c.modifier || '',
    modifiers: c.modifiers || [],
    emCalculator: c.emCalculator,
    active: c.active === undefined ? true : c.active,
    cptCode: c.code || c.cptCode,
    lineNumber: c.lineNumber,
    diagnosisList: c.diagnosisList || [],
    provider: c.provider,
    secondaryProvider: c.secondaryProvider || null,
    tertiaryProvider: c.tertiaryProvider || null,
    serviceDate: dateFromInput(c.serviceDate),
    links: c.links,
    stickyNote: note ? note.note : c.stickyNote,
    multiplier: c.multiplier || 1,
    docBundleVersion: c.docBundleVersion || null,
    serviceLocation: c.serviceLocation || null, 
    fromAutoCoder: c.fromAutoCoder || null,
    autoCoder: c.autoCoder || null,
    autoCoderConfidence: c.autoCoderConfidence || null,
    autoCoderNote: c.autoCoderNote || null,
  };
}

export function removeDxFromCptList(
  toRemove: Diagnosis,
  diagnoses: Diagnosis[],
  procedures: Procedure[],
  replacementLink: string = null,
  removeProcedure = false
): [Diagnosis[], Procedure[]] {
  const code = toRemove?.diagnosisCode;
  if (!code) {
    return [[], []];
  }

  const icd = removeFrom(diagnoses.slice(), (v) => v.diagnosisCode === code);

  const cpt = removeFrom(procedures.slice(), (v) => {
    removeFrom(v.diagnosisList, (u) => u === code, replacementLink);
    return removeProcedure && v.diagnosisList.length === 0;
  });

  return [icd, cpt];
}

type CptPatch = Partial<CodeSet | Procedure>;
export type CptPatchMap = Record<string, (p?: CptPatch) => Procedure>;

export function cptPatchMap(set: Record<string, Procedure>): CptPatchMap {
  const map: CptPatchMap = {};

  Object.keys(set).forEach((key) => {
    map[key] = (p) => procedure({ ...set[key], ...p }, null);
  });

  return map;
}

type CptLinkConf = {
  cptLinks?: boolean;
  emLinks?: boolean;
};
export function updateCptLinks(w: Worksheet, conf?: CptLinkConf) {
  const { cptLinks, emLlinks } = {
    ...{
      cptLinks: true,
      emLlinks: true,
    },
    ...conf,
  };

  if (cptLinks) {
    setAllDiagnosesOnEmptyCpts(w);
  } else {
    setFirstDiagnosisOnEmptyCpts({
      procedures: w.procedures.filter((v) => !v.isEMCode),
      diagnoses: w.diagnoses,
    });
  }

  if (emLlinks) {
    setAllDiagnosesOnEmProcedures(w);
  } else {
    setFirstDiagnosisOnEmptyCpts({
      procedures: w.procedures.filter((v) => v.isEMCode),
      diagnoses: w.diagnoses,
    });
  }
}

export function setProcedureLinksFromDiagnosesPosition(
  procedures: Procedure[],
  diagnoses: Diagnosis[]
): Procedure[] {
  diagnoses.map((a, i) => {
    a.lineNumber = i + 1;
    return a.lineNumber;
  });

  console.log(
    'new icd position',
    diagnoses.map((a) => a.diagnosisCode + ' ' + a.lineNumber).join(',')
  );

  return procedures.map((p: Procedure) => {
    let links = [];
    let diagnosisList = [];

    let size = p?.diagnosisList?.length;
    if (size) {
      p.diagnosisList.forEach((code) => {
        const lineNumber = diagnoses.filter((a) => a.diagnosisCode === code)[0]
          ?.lineNumber;
        if (lineNumber) {
          console.log('found', code, lineNumber);
          links.push(lineNumber);
          diagnosisList.push(code);
        }
      });
    }
    links.sort();

    p.links = links.length ? links.join(',') : '';
    // Rebuild diagnosisList from the same match pass as links, so a diagnosis
    // that is no longer present in `diagnoses` (e.g. deactivated/removed) is
    // dropped from both instead of only being dropped from `links`.
    p.diagnosisList = diagnosisList;
    console.log('new links', p.cptCode, p.links);

    return p;
  });
}

type PostProcessCpts = {
  icdProvider: () => Diagnosis[];
  autoAssignEm?: boolean;
};

export function postProcessCpts(
  conf: PostProcessCpts, 
  sortEm = false
): (a: Procedure[]) => Procedure[] {
  const { icdProvider, autoAssignEm } = { ...{ autoAssignEm: true }, ...conf };

  return (cpts) => {
    const procedures = sortEm ? updateEmProcedures(cpts) : cpts;
    const diagnoses = icdProvider();
    const sheet = { procedures, diagnoses };

    if (autoAssignEm) {
      setAllDiagnosesOnEmProcedures(sheet);
    }

    procedures.forEach((v) => {
      setProcedureDiagnoses(sheet, v);
    });


    console.info('postProcessCpts',procedures.map(v => v.cptCode), procedures == cpts)

    return procedures;
  };
}

function modifierString(a: Procedure): string {
  let result = '';
  if (a?.modifiers?.length) {
    result += a.modifiers.join(',');
  }
  return result;
}

function providerId(a: Procedure): string {
  let result = '';
  if (a?.provider) {
    result += a.provider.providerId;
  }
  if(a.secondaryProvider) {
    result += a.secondaryProvider.providerId;
  }
  if(a.tertiaryProvider) {
    result += a.tertiaryProvider.providerId;
  }
  return result;
}

function serviceDateId(a: Procedure): string {
  let result = '';
  if (a?.serviceDate?.getTime) {
    result += a.serviceDate.getTime();
  }
  return result;
}

export function cptId(a: Procedure): string {
  if (!a) {
    return '';
  }
  
  if(a.isEMCode){
    return a.cptCode;
  }

  let id = a.cptCode + modifierString(a) + providerId(a) + serviceDateId(a);

  if (a?.ndcInformation?.ndcNumber) {
    const { ndcNumber, ndcMeasure, ndcQuantity } = a.ndcInformation;
    id += ndcNumber + ndcMeasure + ndcQuantity;
  }
  // console.log('id', id);
  return id;
}

export function cptEquals(a: Procedure, b: Procedure): boolean {
  return cptId(a) === cptId(b);
}
